import {
  type LensSeverity,
  type LensType,
  lensesKey,
} from "@repo/fika-blog/src/extensions/lenses"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { createMemo, Show } from "solid-js"
import { createEditorTransaction } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { useI18nContext } from "@/i18n/i18n-solid"
import { tipTextClass } from "./lens-menu.css"

interface Props {
  editor: Editor
}

interface PluginState {
  decorations: unknown
  lensType: LensType
}

interface LensInfo {
  lensType: LensType
  severity: LensSeverity
}

export function LensMenu(props: Props) {
  const { LL } = useI18nContext()

  const selection = createEditorTransaction(
    () => props.editor,
    editor => editor?.state.selection,
  )

  const lensInfo = createMemo((): LensInfo | null => {
    const sel = selection()
    if (!sel || !sel.empty) return null

    const state = props.editor.state
    const pluginState = lensesKey.getState(state) as PluginState | undefined
    if (!pluginState?.lensType) return null

    const pos = sel.$from.pos
    const decorations = pluginState.decorations as {
      find: (
        from: number,
        to: number,
      ) => Array<{ spec: { lensType?: LensType; severity?: LensSeverity } }>
    }
    const found = decorations.find(pos, pos)

    if (!found || found.length === 0) return null

    const spec = found[0]?.spec
    if (!spec?.lensType || !spec?.severity) return null

    return { lensType: spec.lensType, severity: spec.severity }
  })

  function getTip(info: LensInfo): string {
    if (info.lensType === "sentence-difficulty") {
      return info.severity === "orange"
        ? LL().proofreader.lenses.tips.veryHard()
        : LL().proofreader.lenses.tips.hard()
    }
    return info.severity === "orange"
      ? LL().proofreader.lenses.tips.veryMonotonous()
      : LL().proofreader.lenses.tips.monotonous()
  }

  return (
    <Show when={lensInfo()}>
      {info => (
        <Mini.Menu>
          <Mini.Section>
            <Mini.Icon name="lightbulb" />
            <span class={tipTextClass}>{getTip(info())}</span>
          </Mini.Section>
        </Mini.Menu>
      )}
    </Show>
  )
}
